import React from 'react'
import ReactModal, { Props } from 'react-modal'
import styled from 'styled-components'
import { IconCross } from '../../iconography/Icons/Icons'
import { defaultTheme } from '../../../theme'

export type ModalSize = 'small' | 'medium' | 'large' | 'responsive'
export type ModalPadding = 'noPadding' | 'paddingExist'

export const ModalRaw: React.FC<Props> = props => {
  return (
    <ReactModal
      style={{
        overlay: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.48)',
          zIndex: defaultTheme.zIndex.modal,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        content: {
          position: 'static',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: 'none',
          background: 'none',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderRadius: 0,
          outline: 'none',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
      ariaHideApp={false}
      {...props}
    />
  )
}

const StyledModalContent = styled.div`
  position: relative;
  background: ${props => props.theme.color.onPrimaryLight};
  box-shadow: 0px 16px 24px rgba(0, 0, 0, 0.04);
  border-radius: 16px;
  width: 96vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.color.onPrimaryDark};

  &[data-padding='noPadding'] {
    padding: 0px !important;
  }

  &[data-size='small'] {
    padding: 16px 40px 40px;
    max-width: 278px;
  }

  &[data-size='medium'] {
    padding: 24px 40px 40px;
    max-width: 480px;
  }

  &[data-size='large'] {
    padding: 32px 40px 40px;
    max-width: 800px;
  }

  &[data-size='responsive'] {
    padding: 16px 40px 40px;
    width: auto;
  }

  ${props => props.theme.media.mobile`
    font-size: 14px;
    line-height: 18px;
  `}
`

const CrossStyled = styled(IconCross)`
  cursor: pointer;
  transition: 0.3s fill;
  position: absolute;
  top: 14px;
  right: 14px;

  :hover {
    fill: ${props => props.theme.color.secondary300};
  }
`

export const ModalHeading = styled.header`
  font-weight: bold;
  font-size: 24px;
  line-height: 32px;
  color: ${props => props.theme.color.onSurfaceHighEmphasys};
  align-self: ${(props: { compact?: boolean }) => (props.compact ? 'flex-start' : 'center')};
  margin: 24px 0px 8px;

  ${props => props.theme.media.mobile`
    font-size: 20px;
    line-height: 38px;
    margin: 16px 0px 8px;
  `}
`

export const ModalWizardHeading = styled.header`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: ${props => props.theme.color.onSurfaceHighEmphasys};
  align-self: ${(props: { compact?: boolean }) => (props.compact ? 'flex-start' : 'center')};
  margin: 24px 0px 8px;

  ${props => props.theme.media.mobile`
  font-size: 20px;
  line-height: 38px;
  margin: 16px 0px 8px;
`}
`

export type ModalProps = {
  isOpen: boolean
  onClose: () => void
  heading?: string | React.ReactElement
  size?: ModalSize
  className?: string
  modalPadding?: ModalPadding
  isWizard?: boolean
}

export const Modal: React.FC<Props & ModalProps> = ({
  children,
  onClose,
  heading,
  size = 'medium',
  className,
  modalPadding,
  isWizard,
  ...props
}) => {
  const ModalHeader = isWizard ? ModalWizardHeading : ModalHeading

  return (
    <ModalRaw shouldCloseOnEsc shouldCloseOnOverlayClick {...props} onRequestClose={onClose}>
      <StyledModalContent data-size={size} data-padding={modalPadding} className={className}>
        <CrossStyled onClick={onClose} />
        {!!heading && <ModalHeader>{heading}</ModalHeader>}
        {children}
      </StyledModalContent>
    </ModalRaw>
  )
}
